import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'devextreme-react';
import { formatTime } from '../utils/stopwatchUtils';
import MultiTouchButton from './MultiTouchButton';

// Stoppuhr-Komponente für einzelne Zellen
const StopwatchCell = ({ 
    athlete, 
    onTimeRecord, 
    isActive, 
    times, 
    onStart, 
    onStop, 
    onRestart,
    rowIndex,
    laneIndex,
    onEdit,
    onSwap,
    swapMode,
    isFirstSwapCell,
    currentTime: centralCurrentTime,
    lapTime: centralLapTime
}) => {
    const [currentTime, setCurrentTime] = useState(centralCurrentTime || 0);
    const [lapTime, setLapTime] = useState(centralLapTime || 0);
    const intervalRef = useRef(null);
    const startTimeRef = useRef(null);
    const lapStartTimeRef = useRef(null);
    const timesListRef = useRef(null);

    // Beim Tausch: Synchronisiere mit zentralen Zeiten
    useEffect(() => {
        setCurrentTime(centralCurrentTime || 0);
        setLapTime(centralLapTime || 0);
    }, [centralCurrentTime, centralLapTime]);

    useEffect(() => {
        if (isActive && !intervalRef.current) {
            // Timer starten
            const now = Date.now();
            startTimeRef.current = now - (centralCurrentTime || 0);
            lapStartTimeRef.current = now - (centralLapTime || 0);
            
            intervalRef.current = setInterval(() => {
                const now = Date.now();
                const newCurrentTime = now - startTimeRef.current;
                const newLapTime = now - lapStartTimeRef.current;
                setCurrentTime(newCurrentTime);
                setLapTime(newLapTime);
            }, 10);
        } else if (!isActive && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isActive, centralCurrentTime, centralLapTime]);

    // Reset Timer when restarting
    useEffect(() => {
        if (isActive && currentTime === 0 && lapTime === 0) {
            const now = Date.now();
            startTimeRef.current = now;
            lapStartTimeRef.current = now;
        }
    }, [isActive, currentTime, lapTime]);

    // Auto-scroll zu neuester Zeit
    useEffect(() => {
        if (timesListRef.current && times && times.length > 0) {
            timesListRef.current.scrollTop = timesListRef.current.scrollHeight;
        }
    }, [times]);

    const handleLapTime = () => {
        // Nimmt die aktuelle Rundenzeit und startet Rundenzeit neu
        const currentLapTime = lapTime;
        onTimeRecord(rowIndex, laneIndex, currentLapTime, 'lap');
        // Rundenzeit zurücksetzen und neu starten
        lapStartTimeRef.current = Date.now();
        setLapTime(0);
    };

    const handleSplitTime = () => {
        // Nimmt die Hauptzeit (Zwischenzeit) und startet Rundenzeit neu
        const splitTime = currentTime;
        onTimeRecord(rowIndex, laneIndex, splitTime, 'split');
        // Rundenzeit zurücksetzen und neu starten
        lapStartTimeRef.current = Date.now();
        setLapTime(0);
    };

    return (
        <div className={`stopwatch-cell ${isActive ? 'active' : ''} ${!athlete ? 'empty' : ''}`}>
            <div className="cell-content">
                {/* Erste Reihe: Name und Buttons (bleibt unverändert) */}
                <div className="athlete-info">
                    <div className="athlete-name">
                        {athlete ? `${athlete.surname} ${athlete.lastname}` : 'Leer'}
                    </div>
                    <div className="athlete-actions">
                        <Button
                            icon="edit"
                            onClick={onEdit}
                            width="20px"
                            height="20px"
                            type="normal"
                            hint="Ändern"
                        />
                        <Button
                            icon={swapMode ? "check" : "refresh"}
                            onClick={onSwap}
                            width="20px"
                            height="20px"
                            type={isFirstSwapCell ? "success" : "normal"}
                            hint={swapMode ? "Tausch" : "Swap"}
                        />
                    </div>
                </div>

                {/* Zweite Reihe: Start/Restart und Stop nebeneinander - nur wenn Sportler ausgewählt */}
                {athlete && (
                    <div className="control-buttons-row">
                        <MultiTouchButton
                            text={isActive ? "Restart" : "Start"}
                            type="success"
                            onClick={isActive ? onRestart : onStart}
                            width="70%"
                            height="32px"
                            className="multitouch-button--start-stop"
                        />
                        <MultiTouchButton
                            text="Stop"
                            type="danger"
                            onClick={onStop}
                            width="28%"
                            height="32px"
                            disabled={!isActive}
                            className="multitouch-button--start-stop"
                        />
                    </div>
                )}

                {/* Dritte Reihe: Zeit-Display und aufgezeichnete Zeiten links, Buttons rechts - nur wenn Sportler ausgewählt */}
                {athlete && (
                    <div className="time-display-and-actions-row">
                        <div className="left-time-section">
                            <div className="time-display">
                                <div className="main-time">{formatTime(currentTime)}</div>
                                <div className="lap-time">R: {formatTime(lapTime)}</div>
                            </div>
                            <div className="recorded-times-section">
                                {times && times.length > 0 && (
                                    <div className="times-list" ref={timesListRef}>
                                        {times.map((time, index) => (
                                            <div key={index} className="time-entry">
                                                <span className="time-number">{index + 1}.</span>
                                                <span className="time-type">{time.type === 'lap' ? 'R' : 'Z'}:</span>
                                                <span className="time-value">{formatTime(time.time)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="right-action-buttons">
                            <MultiTouchButton
                                text="Zeit"
                                type="primary"
                                disabled={!isActive}
                                onClick={handleSplitTime}
                                width="100%"
                                height="auto"
                                className="multitouch-button--time-record zeit-button-large"
                            />
                            <MultiTouchButton
                                text="R"
                                type="warning"
                                disabled={!isActive}
                                onClick={handleLapTime}
                                width="100%"
                                height="36px"
                                className="multitouch-button--lap r-button-small"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StopwatchCell;
